/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 * @author Nikolay Sviridov
 * @version 1.35.0
 * @since 1.35.0
 */

import { useEffect, useRef, useState } from 'react';
import type { FC, ChangeEvent, KeyboardEvent, RefObject } from 'react';

import { TimePickerClocks, Typography } from '@nexign/scc-ui-kit';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import InputMask from 'react-input-mask';

import { KeyboardKeyEnum } from '@/main/modules/app/appConstants';
import ICommonTranslation from '@/main/modules/internationalization/types/common';
import { icons } from '@assets/index';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

import { getTimeZone } from '../../Table/helpers/getTimeZone';
import { DropDownFilter as DropDownFilterComponent } from '../DropDownFilter/DropDownFilter';
import { DropDownFilter, Input } from '../index';
import { EInputType } from '../Input/Input';
import { TimePickerWithClocksDropdownStyled } from './styled/TimePickerWithClocksStyled';

interface ITimePickerWithClocksProps {
    defaultTimeValueHHmm?: string;
    onSelectTime: (time: Dayjs) => void;
    translation: ICommonTranslation;
}

export const TimePickerWithClocks: FC<ITimePickerWithClocksProps> = ({ 
    defaultTimeValueHHmm, onSelectTime, translation,
}) => {

    // чтобы можно было работать отдельно со временем
    dayjs.extend(customParseFormat); 

    const timezoneOffset = getTimeZone(new Date().toISOString());

    const { calendar: { time: timeTranslation } } = translation;

    const defaultValue = !!defaultTimeValueHHmm && dayjs(defaultTimeValueHHmm, EFormatData.OnlyHourMinutes).isValid() 
        ? dayjs(defaultTimeValueHHmm, EFormatData.OnlyHourMinutes)
        : dayjs('00:00', EFormatData.OnlyHourMinutes)

    const [inputValue, setInputValue] = useState<string>('');
    const [time, setTime] = useState<Dayjs | null>(defaultValue);

    const dropDownRef: RefObject<DropDownFilterComponent> = useRef<DropDownFilterComponent>(null);

    /*
     * -------------------------------------------
     *               Handlers
     * -------------------------------------------
     */

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInputValue(value);
    };

    const onCloseClick = () => {
        const isValid =
            dayjs(inputValue, EFormatData.OnlyHourMinutes, true).isValid();

        // при закрытии выпадающего меню, если введенная дата валидна, то сохраняем значение
        if (isValid) {
            onSelectTime(dayjs(inputValue, EFormatData.OnlyHourMinutes));
            setTime(dayjs(inputValue, EFormatData.OnlyHourMinutes));
            setInputValue(inputValue);
        } else {
            setInputValue(defaultValue.format(EFormatData.OnlyHourMinutes));
        }

    };

    const onInputKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === KeyboardKeyEnum.ENTER || event.key === KeyboardKeyEnum.TAB) {
                onCloseClick();
            }
        };

    const onInputBlur = () => {
        if (!dropDownRef.current?.state.isToolTipShown) {
            onCloseClick();
        }
    };

    useEffect(() => {
        setInputValue(time?.format(EFormatData.OnlyHourMinutes) ?? '')
    }, [time]);
    /*
     * -------------------------------------------
     *           Render component
     * -------------------------------------------
     */

    const renderButton = () => (
        <Input righticon={icons.timeIconGrey} type={EInputType.small}>
            <InputMask
                mask='99:99'
                placeholder={timeTranslation.timepicker}
                role="textbox"
                value={inputValue}
                onBlur={onInputBlur}
                onChange={onInputChange}
                onKeyDown={onInputKeyDownHandler}
            />
        </Input>
    );

    return (
        <TimePickerWithClocksDropdownStyled data-name={'time-picker-clocks'}>
            <DropDownFilter
                widthAuto
                closeOnFilterButtonClick={false}
                filterButtonLabel={inputValue}
                ref={dropDownRef}
                renderButton={renderButton}
                onCloseClick={onCloseClick}
            >
                <TimePickerClocks
                    showViewSwitcher
                    setTime={setTime}
                    time={time}
                />
                
            </DropDownFilter>
            <Typography>{`${timeTranslation.timezone} ${timezoneOffset}`}</Typography>
        </TimePickerWithClocksDropdownStyled>
    );
};
